import { useCallback, useEffect, useState, useRef } from 'react';
import { api, ApiFailure } from '../lib/api';
import { CONTACT_ERROR_TAGS, CONTACT_ERROR_TAG_KEYS, contactErrorLabel } from '@delivery/shared';
import { AddressSearchBox } from '../components/AddressSearchBox';

interface Row {
  id: string;
  name: string;
  phone: string;
  wrongPhones: string[];
  address: string;
  addressDetail: string | null;
  lat: number | null;
  lng: number | null;
  addressVerified: boolean;
  errorTag: string | null;
  errorNote: string | null;
  /** 派送备注 —— 这个人长期成立的交代,每次用它下单会自动带到订单上 */
  deliveryNote: string | null;
  orderCount: number;
  lastUsedAt: string | null;
  customer: { id: string; name: string } | null;
}

/** 一堆「可能是同一个人」的记录。`reason` 说清楚系统是拿什么比出来的 */
interface DupGroup {
  contacts: Row[];
  reason: 'GEO' | 'ADDRESS';
}

const INPUT =
  'rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800';

function ContactPhoneCell({ phone, wrongPhones }: { phone: string; wrongPhones: string[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-w-0 max-w-[150px] sm:max-w-[180px]">
      <div className="font-mono text-xs font-medium text-slate-800 dark:text-slate-200 truncate select-all" title={phone}>
        {phone}
      </div>
      {wrongPhones.length === 1 && (
        <div
          className="font-mono text-[11px] text-slate-400 truncate select-all mt-0.5"
          title={`旧号: ${wrongPhones[0]}`}
        >
          旧: {wrongPhones[0]}
        </div>
      )}
      {wrongPhones.length > 1 && (
        <div className="mt-0.5">
          <div className="flex items-center gap-1 font-mono text-[11px] text-slate-400">
            <span
              className="truncate max-w-[85px] sm:max-w-[105px] select-all"
              title={`旧号: ${wrongPhones[0]}`}
            >
              旧: {wrongPhones[0]}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              className="inline-flex shrink-0 items-center rounded bg-slate-100 px-1 py-0.5 font-sans text-[10px] font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              title={open ? '收起旧号' : '展开查看全部旧号'}
            >
              {open ? '收起' : `+${wrongPhones.length - 1}`}
            </button>
          </div>
          {open && (
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/95 p-1.5 text-[11px] shadow-sm dark:border-slate-700 dark:bg-slate-800/95">
              <div className="mb-1 flex items-center justify-between border-b border-slate-200/60 pb-0.5 text-[10px] text-slate-400 dark:border-slate-700">
                <span>全部旧号 ({wrongPhones.length})</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                  }}
                  className="font-sans text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  收起
                </button>
              </div>
              <div className="max-h-24 space-y-0.5 overflow-y-auto font-mono">
                {wrongPhones.map((p) => (
                  <div key={p} className="select-all text-slate-600 dark:text-slate-300 truncate" title={p}>
                    {p}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 收件人通讯录。
 *
 * ★ 这一页存在的理由不是「管理数据」,是**让建单少问一遍**。
 *   所以它最重要的两个动作是:把送不到的那些标出来(下次建单当场看得见),
 *   以及把坐标补上(没坐标的单上不了地图、司机导航不了)。
 */
interface Report {
  id: string;
  errorTag: string | null;
  note: string | null;
  status: string;
  reportedAt: string;
  reportedBy: string;
  contact: {
    id: string;
    name: string;
    phone: string;
    address: string;
    currentTag: string | null;
    approvedBefore: number;
  };
}

/**
 * 司机报上来的错误标记 —— 批了才生效。
 *
 * ★ 为什么要审:这条标记会影响之后**每一次**给这个人建单
 *   (客服看到「电话有问题」会先打电话确认,甚至不敢发单)。
 *   而司机在门口的判断有时候是错的:打错号、走错门、
 *   按门铃没人应,都会被记成「电话打不通」。
 *
 * ★ 每条都要摆出「谁报的、哪一单、以前被批过几次」——
 *   审的人没有依据就只能全批或全驳,而这一屏就废了。
 */
function ErrorReports({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ total: number; data: Report[] }>(
        '/contacts/error-reports?status=PENDING',
      );
      setRows(r.data);
      setTotal(r.total);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function review(id: string, approve: boolean) {
    setBusy(id);
    try {
      await api(`/contacts/error-reports/${id}/review`, { method: 'POST', body: { approve } });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '操作失败');
    } finally {
      setBusy(null);
    }
  }

  if (rows.length === 0) return null;

  return (
    <section
      aria-label="待审核的错误标记"
      className="rounded-xl border border-amber-200 bg-amber-50/60 p-4
                 dark:border-amber-900 dark:bg-amber-950/30"
    >
      <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
        司机报上来的问题({total} 条待审)
      </div>
      <p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-300/80">
        批了才会生效 —— 生效之后,客服每次给这个人建单都会看到
      </p>

      {err && <div className="mt-2 text-sm text-red-700">{err}</div>}

      <ul className="mt-3 max-h-36 space-y-2 overflow-y-auto pr-1">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-white px-3 py-2
                       text-sm dark:bg-slate-800"
          >
            <span className="font-medium text-slate-900 dark:text-slate-100">{r.contact.name}</span>
            <span className="font-mono text-xs text-slate-500">{r.contact.phone}</span>
            <span className="text-slate-500">{r.contact.address}</span>
            <span
              className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700
                             dark:bg-red-900/40 dark:text-red-300"
            >
              {r.errorTag ? contactErrorLabel(r.errorTag) : '核对过,没问题'}
            </span>
            {r.note && <span className="text-xs text-slate-400">{r.note}</span>}
            <span className="text-xs text-slate-400">
              {r.reportedBy} ·{' '}
              {new Date(r.reportedAt).toLocaleString('zh-CN', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {/* ★ 反复被报是很强的证据 —— 审的人最需要的就是这一项 */}
            {r.contact.approvedBefore > 0 && (
              <span className="text-xs text-amber-700 dark:text-amber-400">
                以前批过 {r.contact.approvedBefore} 次
              </span>
            )}
            {canManage && (
              <span className="ml-auto flex gap-2">
                <button
                  onClick={() => void review(r.id, true)}
                  disabled={busy === r.id}
                  className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-medium text-white
                             disabled:opacity-40"
                >
                  通过
                </button>
                <button
                  onClick={() => void review(r.id, false)}
                  disabled={busy === r.id}
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs text-slate-600
                             disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"
                >
                  驳回
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

type SearchField = 'all' | 'name' | 'address' | 'phone';
/** [取值, 按钮上的字, 搜索框的提示] */
const SEARCH_FIELDS: ReadonlyArray<readonly [SearchField, string, string]> = [
  ['all', '全部', '姓名 / 电话 / 地址'],
  ['name', '姓名', '只搜姓名'],
  ['address', '地址', '只搜地址(含楼层门牌)'],
  ['phone', '电话', '只搜电话,打几位数字就行'],
];

export function Contacts({ permissions }: { permissions: string[] }) {
  /*
   * ★ 这一屏原来根本不看权限:审核按钮写死显示、编辑按钮人人可见,
   *   而后端守着 `contact:manage` —— 点下去才报「无权限」。
   *   按钮存在但一按就报错,比没有这颗按钮更糟。
   */
  const canManage = permissions.includes('contact:manage');
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  /*
   * 只在哪一栏里搜。默认「全部」= 原来的规则(姓名 + 地址 + 像号码时的电话)。
   * ★ 记得的是地址里的一段、而这段字又正好出现在一堆人名里时,
   *   全部一起搜会出来几十条 —— 只搜地址就只剩地址里有这段字的。
   */
  const [field, setField] = useState<SearchField>('all');
  const [errorOnly, setErrorOnly] = useState(false);
  const [noCoordsOnly, setNoCoordsOnly] = useState(false);
  /*
   * ★ 六千多条,而这一屏原来只取前 50 —— 后面的人**根本不存在**。
   *
   *   最坑的地方是它不报错也不提示:界面上就是一张 50 行的表,
   *   看起来像「一共就这么多」。而「共 6142 人」那个数字就在旁边,
   *   两个数对不上也没人会盯着看。
   */
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  /*
   * ★ 「可能重复」是**筛子,不是扫把**:它只把同一个地方的几条摆到一起,
   *   删哪条、留哪条、还是两条都留,是坐在这儿的人的判断。
   *
   *   自动合并看着省事,代价是它会悄悄改名、悄悄删记录 ——
   *   两条名字不一样时按「哪个写法出现得多」定,而那跟谁对没关系。
   */
  const [dupOnly, setDupOnly] = useState(false);
  const [groups, setGroups] = useState<DupGroup[]>([]);
  const [truncated, setTruncated] = useState(false);
  /** 勾中的那些人(publicId)。换筛选、翻页都清空 —— 手上拿着看不见的选择最容易删错 */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirmDel, setConfirmDel] = useState<Row[] | null>(null);
  const [merging, setMerging] = useState<Row[] | null>(null);
  const DUP_PAGE_SIZE = 20;
  const [editing, setEditing] = useState<Row | null>(null);
  const [tagging, setTagging] = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** 刚建完的那条 —— 建完把搜索框设成它的号码,不然它掉进六千条里找不着 */
  const [justSaved, setJustSaved] = useState<string | null>(null);

  /*
   * ★ 只认**最后一次**请求的结果。
   *
   *   边打边搜的时候,先发出去的那个「不带筛选」的请求
   *   (七千多条里取 50)完全可能比后发的筛选请求晚回来 ——
   *   然后把搜索结果盖成整本名册。表现是「搜着搜着结果自己跳回去了」:
   *   偶发、不报错,而且数据越多越容易撞上。
   */
  /*
   * ★ 自动合并分两步:先试算给数,再确认。
   *   合并不可逆(输家被软删、号码降成旧号),而一次可能动几百条 ——
   *   让人先看见数字再点第二下。
   */
  const [autoPlan, setAutoPlan] = useState<{ groups: number; willMerge: number } | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoDone, setAutoDone] = useState<string | null>(null);

  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++reqSeq.current;
    try {
      if (dupOnly) {
        const qs = new URLSearchParams({
          limit: String(DUP_PAGE_SIZE),
          offset: String(page * DUP_PAGE_SIZE),
        });
        const r = await api<{ total: number; truncated: boolean; data: DupGroup[] }>(
          `/contacts/duplicates?${qs.toString()}`,
        );
        if (mine !== reqSeq.current) return;
        setGroups(r.data);
        setTotal(r.total);
        setTruncated(r.truncated);
        setErr(null);
        return;
      }
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (q.trim()) qs.set('q', q.trim());
      if (q.trim() && field !== 'all') qs.set('field', field);
      if (errorOnly) qs.set('errorOnly', 'true');
      if (noCoordsOnly) qs.set('noCoordsOnly', 'true');
      const r = await api<{ total: number; data: Row[] }>(`/contacts?${qs.toString()}`);
      if (mine !== reqSeq.current) return; // 已经有更新的请求了,这份是过期的
      setRows(r.data);
      setTotal(r.total);
      setErr(null);
    } catch (e) {
      if (mine !== reqSeq.current) return;
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, [q, field, errorOnly, noCoordsOnly, dupOnly, page]);

  /** 试算:找出三样都一样的几组,不动数据 */
  async function previewAutoMerge(): Promise<void> {
    setAutoBusy(true);
    setAutoDone(null);
    try {
      const r = await api<{ groups: number; willMerge: number }>('/contacts/auto-merge', {
        method: 'POST',
        body: {},
      });
      setAutoPlan({ groups: r.groups, willMerge: r.willMerge });
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '试算失败');
    } finally {
      setAutoBusy(false);
    }
  }

  /** 真合并 —— 只在人看过试算的数字之后才可能走到这里 */
  async function applyAutoMerge(): Promise<void> {
    setAutoBusy(true);
    try {
      const r = await api<{ merged: number; failed: unknown[] }>('/contacts/auto-merge', {
        method: 'POST',
        body: { apply: true },
      });
      /*
       * ★ 失败的组要**说出来**,不能只报成功数。
       *   只说「并掉了 8 条」的话,人回头看到列表里还有重复,
       *   而屏幕上没有任何地方解释为什么。
       */
      const failed = r.failed?.length ?? 0;
      setAutoDone(
        failed > 0 ? `并掉了 ${r.merged} 条,有 ${failed} 组没并上` : `并掉了 ${r.merged} 条`,
      );
      setAutoPlan(null);
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '合并失败');
    } finally {
      setAutoBusy(false);
    }
  }

  /**
   * 删掉勾中的这些人。
   *
   * ★ 删完**不留着上一次的勾**:那些行已经没了,而勾还在的话
   *   下一次点删除删的是一批看不见的东西。
   */
  async function deletePicked(targets: Row[]) {
    try {
      await api<{ deleted: number }>('/contacts/batch-delete', {
        method: 'POST',
        body: { ids: targets.map((t) => t.id) },
      });
      setConfirmDel(null);
      setPicked(new Set());
      setJustSaved(null);
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '删除失败');
      setConfirmDel(null);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  /*
   * ★ 改了搜索或筛选就回第一页。
   *   不回的话:在第 8 页搜一个只有 3 条结果的名字,
   *   屏幕上是**一片空白** —— 看起来像「搜不到」。
   */
  useEffect(() => {
    setPage(0);
  }, [q, field, errorOnly, noCoordsOnly, dupOnly]);

  /*
   * ★ 换筛选、翻页就把勾清掉。
   *   留着的话人看不见自己还拿着什么 —— 然后按下的那颗「删除 7 条」
   *   里有 5 条是上一页的、他早就忘了的。
   */
  useEffect(() => {
    setPicked(new Set());
  }, [q, field, errorOnly, noCoordsOnly, dupOnly, page]);

  const noCoords = rows.filter((r) => r.lat == null).length;
  /** 当前屏幕上摆着的人 —— 勾选、全选都只认这些 */
  const visible = dupOnly ? groups.flatMap((g) => g.contacts) : rows;
  const pickedRows = visible.filter((r) => picked.has(r.id));
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** 表格的一行 —— 平铺和按堆分组共用一个,免得两边慢慢长成两副样子 */
  const row = (r: Row) => (
    <tr
      key={r.id}
      className={`border-t border-slate-100 dark:border-slate-700
                  max-lg:relative max-lg:block max-lg:space-y-0.5 max-lg:py-3 max-lg:pr-3
                  ${canManage ? 'max-lg:pl-11' : 'max-lg:pl-3'} ${
        picked.has(r.id) ? 'bg-brand-50/60 dark:bg-brand-950/30' : ''
      }`}
    >
      {canManage && (
        <td className="px-3 py-2 max-lg:absolute max-lg:left-0 max-lg:top-3 max-lg:py-0">
          <input
            type="checkbox"
            aria-label={`选择 ${r.name}`}
            checked={picked.has(r.id)}
            onChange={() => toggle(r.id)}
          />
        </td>
      )}
      <td className="px-3 py-2 text-slate-900 dark:text-slate-100 max-lg:block max-lg:p-0 max-lg:font-medium">{r.name}</td>
      <td className="px-3 py-2 w-36 sm:w-44 max-w-[180px] align-top max-lg:block max-lg:w-auto max-lg:max-w-none max-lg:p-0">
        <ContactPhoneCell phone={r.phone} wrongPhones={r.wrongPhones} />
      </td>
      <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-lg:block max-lg:p-0">
        {r.address}
        {r.addressDetail && <span className="text-slate-400"> {r.addressDetail}</span>}
      </td>
      {/* 手机上有坐标就不占一行 —— 卡片上只提醒「缺坐标」这件要处理的事 */}
      <td className={`px-3 py-2 font-mono text-xs max-lg:p-0 ${r.lat != null ? 'max-lg:hidden' : 'max-lg:block'}`}>
        {r.lat != null ? (
          <span className="text-green-600 dark:text-green-400">
            {r.lat.toFixed(4)}, {r.lng!.toFixed(4)}
          </span>
        ) : (
          <span className="text-amber-600 dark:text-amber-400">
            <span className="lg:hidden">坐标:</span>没有
          </span>
        )}
      </td>
      <td className={`px-3 py-2 max-lg:p-0 ${r.errorTag || r.errorNote ? 'max-lg:block' : 'max-lg:hidden'}`}>
        {r.errorTag && (
          <span
            className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700
                           dark:bg-red-900/40 dark:text-red-300"
          >
            {contactErrorLabel(r.errorTag)}
          </span>
        )}
        {r.errorNote && <div className="text-xs text-slate-400">{r.errorNote}</div>}
      </td>
      <td className="px-3 py-2 text-slate-500 max-lg:inline max-lg:p-0 max-lg:text-xs">
        <span className="lg:hidden">下过 </span>
        {r.orderCount}
        <span className="lg:hidden"> 单</span>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap max-lg:inline max-lg:p-0 max-lg:pl-3">
        {canManage && (
          <>
            <button
              onClick={() => setTagging(r)}
              className="text-xs text-brand-600 hover:underline dark:text-brand-400"
            >
              标记
            </button>
            <button
              onClick={() => setEditing(r)}
              className="ml-3 text-xs text-brand-600 hover:underline dark:text-brand-400"
            >
              编辑
            </button>
            <button
              onClick={() => setConfirmDel([r])}
              className="ml-3 text-xs text-red-600 hover:underline dark:text-red-400"
            >
              删除
            </button>
          </>
        )}
      </td>
    </tr>
  );

  return (
    /*
      ★ 只在桌面上把页面钉成一屏高、让表格自己滚(表头和上面的筛选一直在眼前)。
        手机上不行:说明、待审面板、筛选就占掉大半屏,列表只剩一条缝在里面滚。
        手机上整页一起滚 —— 上面那些滑走,列表用满整屏。
    */
    <div className="flex flex-col p-4 lg:h-screen lg:p-6 lg:overflow-hidden">
      <div className="shrink-0 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">收件人</h1>
            <p className="mt-1 text-sm text-slate-500">
              建单时自动记下来的。下次接到同一个人的电话,在建单页顶上搜一下就能填全
            </p>
          </div>
          {/*
            ★ 「先录人、再建单」是真实存在的一步:商家发来一批新客户,
              或者电话里先问清楚地址、单子晚点再下。
              原来这一屏只能等建单时**顺带**记下来 —— 没有别的入口。
          */}
          {canManage && (
            <button
              onClick={() => setCreating(true)}
              className="h-10 shrink-0 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white"
            >
              + 新增收件人
            </button>
          )}
        </div>

        <ErrorReports canManage={canManage} />

        <div className="flex flex-wrap items-center gap-3">
          <input
            aria-label="搜索收件人"
            placeholder={SEARCH_FIELDS.find(([k]) => k === field)![2]}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setJustSaved(null); // 人一动搜索,那条「已加进通讯录」就过期了
            }}
            className={`${INPUT} w-72`}
          />
          {/* 搜索范围:单选,点一下就换 —— 要的是「只在这一栏里找」,不是几栏的组合 */}
          <div
            role="radiogroup"
            aria-label="搜索范围"
            className="inline-flex overflow-hidden rounded-lg border border-slate-300 text-sm dark:border-slate-600"
          >
            {SEARCH_FIELDS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={field === key}
                onClick={() => setField(key)}
                className={`px-3 py-1.5 ${
                  field === key
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={errorOnly}
              onChange={(e) => {
                setErrorOnly(e.target.checked);
                if (e.target.checked) setDupOnly(false);
              }}
            />
            只看标记过有问题的
          </label>
          <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={noCoordsOnly}
              onChange={(e) => {
                setNoCoordsOnly(e.target.checked);
                if (e.target.checked) setDupOnly(false);
              }}
            />
            只看无GPS坐标的
          </label>
          {/*
            ★ 和搜索框并列,而不是藏进某个菜单:通讯录乱不乱,
              是看着它的人**顺手**该能查的一件事。
          */}
          <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={dupOnly}
              onChange={(e) => {
                setDupOnly(e.target.checked);
                if (e.target.checked) {
                  setErrorOnly(false);
                  setNoCoordsOnly(false);
                }
              }}
            />
            只看可能重复的
          </label>
          <span className="text-sm text-slate-400">
            {dupOnly ? `${total} 堆可能重复` : `共 ${total} 人`}
          </span>
          {/*
            ★ 把「有多少条没坐标」摆在明面上。
              这些人下次建单照样会建出一张上不了地图的单,
              而不主动数一遍的话,没有人会发现这件事。
          */}
          {noCoords > 0 && !noCoordsOnly && (
            <button
              type="button"
              onClick={() => {
                setNoCoordsOnly(true);
                setDupOnly(false);
              }}
              className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 hover:bg-amber-200 transition-colors
                             dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 text-left"
              title="点击只看无GPS坐标的联系人"
            >
              这一页有 {noCoords} 条没有坐标 —— 点击筛选
            </button>
          )}
        </div>

        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {dupOnly && (
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm
                          text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
          >
            {/*
              ★ 先说清楚这一屏**不会**替人做什么。
                「查重」这个词在别的系统里常常连着「一键合并」,
                而合并会按某种规则挑名字、删记录 —— 那些规则跟谁对没关系。
            */}
            同一个地方有好几条记录的,都摆在这儿了 —— <b>需要挑的那些,系统不动</b>。
            有的是同一个人录了两遍,有的是合租、公司总机下面的两个人,分得出来的只有你。
            {/*
              ★ 只有「名字、电话、地址三样都一样」的那些才给一键合并。
                这一档不需要替人挑任何东西 —— 名字也一样,所以不存在
                「挑了个错名字,下一张小票就印错」这件事。
                名字不一样、或者归属不同商家的,仍然留在下面等人看。
            */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                onClick={() => void previewAutoMerge()}
                disabled={autoBusy}
                className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs text-slate-700
                           disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"
              >
                {autoBusy ? '算着…' : '找出三样都一样的'}
              </button>
              {autoPlan && autoPlan.groups > 0 && (
                <>
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    找到 <b>{autoPlan.groups}</b> 组完全相同的,可以并掉 <b>{autoPlan.willMerge}</b>{' '}
                    条(单数会累加到留下那条上)
                  </span>
                  <button
                    onClick={() => void applyAutoMerge()}
                    disabled={autoBusy}
                    className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-medium text-white
                               disabled:opacity-40"
                  >
                    确认合并
                  </button>
                </>
              )}
              {autoPlan && autoPlan.groups === 0 && (
                <span className="text-xs text-slate-500">没有三样都一样的 —— 下面这些都得人看</span>
              )}
              {autoDone && (
                <span className="text-xs text-green-700 dark:text-green-400">{autoDone}</span>
              )}
            </div>
            {truncated && (
              <div className="mt-1 text-amber-700 dark:text-amber-400">
                重复的记录太多,这次只查了前 2000 条 —— 先处理掉一批再看一次
              </div>
            )}
          </div>
        )}

        {justSaved && (
          <div
            aria-live="polite"
            className="flex items-center gap-3 rounded-lg bg-green-50 px-3 py-2 text-sm
                       text-green-800 dark:bg-green-950/40 dark:text-green-300"
          >
            <span>{justSaved} —— 下面这一行就是</span>
            <button onClick={() => setJustSaved(null)} className="ml-auto text-xs text-green-700/70">
              知道了
            </button>
          </div>
        )}
      </div>

      {/*
        ★ 选中之后这条工具条出现在表格上方,而且写死了到底选了几条:
          按下「删除」之前,这个数字是唯一能对一眼的东西。

        ★ 手机上粘在顶栏下面,和订单页的批量条一样 —— 勾了就在眼前,
          不用滑回顶上去找按钮。它得是整页的直接子元素:放在上面那块里的话,
          那块滑出屏幕它就跟着走了(sticky 只在父元素的范围里粘)。
          背景用实色:半透明的话,滑动时下面的卡片会从它身后透出来。
      */}
      {canManage && pickedRows.length > 0 && (
        <div
          aria-label="批量操作"
          className="sticky top-[var(--mobile-bar-h)] z-20 mt-3 flex shrink-0 flex-wrap items-center gap-3
                     rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm shadow-sm
                     lg:static dark:border-brand-800 dark:bg-brand-950"
        >
          <span className="font-medium text-brand-800 dark:text-brand-200">
            选中 {pickedRows.length} 条
          </span>
          {/*
            ★ 合并要**两条以上**才有意义,所以少于两条时这颗按钮不出现 ——
              而不是出现了点下去再报一句「至少选两条」。
          */}
          {pickedRows.length >= 2 && (
            <button
              onClick={() => setMerging(pickedRows)}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm"
            >
              合并成一条
            </button>
          )}
          <button
            onClick={() => setConfirmDel(pickedRows)}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm"
          >
            删除选中的
          </button>
          <button
            onClick={() => setPicked(new Set())}
            className="text-xs text-slate-500 hover:underline dark:text-slate-400"
          >
            取消选择
          </button>
        </div>
      )}

      {/*
        ★ 手机上每一行排成一张卡片(下面那些 max-lg: 就是干这个的)。
          640px 宽的表格塞进手机,要么横着滑、要么地址被挤成五六行 ——
          而这一屏是拿来一条条核对的。用同一份 <tr>,不另写一套卡片:
          两套迟早长成两副样子(某个按钮只加在了一边)。
          表头在手机上收起,全选挪到列表上面。
      */}
      {canManage && visible.length > 0 && (
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 lg:hidden dark:text-slate-300">
          <input
            type="checkbox"
            checked={pickedRows.length === visible.length}
            onChange={(e) =>
              setPicked(e.target.checked ? new Set(visible.map((r) => r.id)) : new Set())
            }
          />
          {/* 不叫「全选这一页」:表头里那个已经叫这个名字,桌面上它只是藏起来、还在页面里 */}
          全选本页
        </label>
      )}
      <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 lg:flex-1 lg:min-h-0 lg:overflow-auto">
        <table className="w-full text-sm max-lg:block lg:min-w-[640px]">
          <thead className="max-lg:hidden sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <tr>
              {canManage && (
                <th className="sticky top-0 z-10 w-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                  {/*
                    ★ 全选只选**这一屏看得见的**。
                      「选中全部 6142 人」这种按钮不给 —— 通讯录里没有
                      「全删」这种正当操作,而手滑点到的代价是整本名册。
                  */}
                  <input
                    type="checkbox"
                    aria-label="全选这一页"
                    checked={visible.length > 0 && pickedRows.length === visible.length}
                    onChange={(e) =>
                      setPicked(e.target.checked ? new Set(visible.map((r) => r.id)) : new Set())
                    }
                  />
                </th>
              )}
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">姓名</th>
              <th className="sticky top-0 z-10 w-36 sm:w-44 max-w-[180px] border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">电话</th>
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">地址</th>
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">坐标</th>
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">标记</th>
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">下过单</th>
              <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"></th>
            </tr>
          </thead>
          <tbody className="max-lg:block">
            {visible.length === 0 && (
              <tr className="max-lg:block">
                <td colSpan={canManage ? 8 : 7} className="max-lg:block px-3 py-6 text-center text-slate-400">
                  {dupOnly
                    ? '没有找到可能重复的 —— 同一个地方只有一条记录'
                    : q.trim() || errorOnly || noCoordsOnly
                      ? '没有符合条件的收件人'
                      : '还没有收件人 —— 建一单就会自动记进来,也可以用右上角直接加'}
                </td>
              </tr>
            )}
            {dupOnly
              ? groups.flatMap((g, gi) => [
                  /*
                    ★ 每堆前面加一行小标题,写清楚**为什么算一堆**。
                      不写的话人看到的是一串挨着的行,得自己比坐标、比地址 ——
                      而那正是这一屏本来要替他做的那部分。
                  */
                  <tr key={`g${gi}`} className="max-lg:block border-t-2 border-slate-300 dark:border-slate-600">
                    <td
                      colSpan={canManage ? 8 : 7}
                      className="max-lg:block bg-slate-50 px-3 py-1.5 text-xs text-slate-500 dark:bg-slate-800/70"
                    >
                      这 {g.contacts.length} 条
                      {g.reason === 'GEO' ? '坐标落在同一个点上' : '地址写的是同一个'}
                      {canManage && (
                        <button
                          onClick={() => setMerging(g.contacts)}
                          className="ml-3 text-brand-600 hover:underline dark:text-brand-400"
                        >
                          合并这堆
                        </button>
                      )}
                    </td>
                  </tr>,
                  ...g.contacts.map((r) => row(r)),
                ])
              : rows.map((r) => row(r))}
          </tbody>
        </table>
      </div>

      {/*
        ★ 翻页条。
          写「第 51–100 条,共 6142 条」而不是只写页码 ——
          页码回答不了「我看到哪儿了」,而这一屏是拿来一条条核对的。
      */}
      {total > (dupOnly ? DUP_PAGE_SIZE : PAGE_SIZE) && (
        <div className="mt-3 shrink-0 flex items-center gap-3 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-slate-700
                       disabled:opacity-40 dark:border-slate-600 dark:text-slate-200"
          >
            上一页
          </button>
          {/* 查重那一屏翻的是**堆**,不是条 —— 单位写错的话数字对不上,人会以为漏了 */}
          <span className="text-slate-500" aria-live="polite">
            第 {page * (dupOnly ? DUP_PAGE_SIZE : PAGE_SIZE) + 1}–
            {Math.min((page + 1) * (dupOnly ? DUP_PAGE_SIZE : PAGE_SIZE), total)}
            {dupOnly ? ` 堆,共 ${total} 堆` : ` 条,共 ${total} 条`}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * (dupOnly ? DUP_PAGE_SIZE : PAGE_SIZE) >= total}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-slate-700
                       disabled:opacity-40 dark:border-slate-600 dark:text-slate-200"
          >
            下一页
          </button>
        </div>
      )}

      {merging && (
        <MergeDialog
          rows={merging}
          onCancel={() => setMerging(null)}
          onDone={async (msg) => {
            setMerging(null);
            setPicked(new Set());
            setJustSaved(msg);
            await load();
          }}
        />
      )}

      {confirmDel && (
        <DeleteConfirmDialog
          rows={confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => void deletePicked(confirmDel)}
        />
      )}

      {creating && (
        <CreateDialog
          onClose={() => setCreating(false)}
          onSaved={async (r) => {
            setCreating(false);
            /*
             * ★ 建完把搜索框设成这个号码。
             *   不这么做的话,新建的那条会掉进六千多条里的某一页 ——
             *   人点完「保存」看着一张没变化的表,不知道到底存没存进去。
             */
            setQ(r.phone);
            setErrorOnly(false);
            setJustSaved(
              r.revived ? '这个人通讯录里本来就有,已经按你填的更新了' : '已经加进通讯录',
            );
          }}
        />
      )}
      {tagging && (
        <ErrorTagDialog
          row={tagging}
          onClose={() => setTagging(null)}
          onSaved={async () => {
            setTagging(null);
            await load();
          }}
        />
      )}
      {editing && (
        <EditDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * 合并成一条。
 *
 * ★ 以哪条为准是**人选的**,不给系统去挑。
 *   「哪个名字对」跟这条下过几单、建得早不早都没关系,
 *   而屏幕前这个人多半刚打过电话,他知道。
 *   默认停在下单最多的那条上 —— 那只是个起点,不是结论。
 *
 * ★ 把「合并之后会发生什么」逐条写出来,而不是一句「确定合并?」。
 *   这一步不可逆:那几条的号码会降成旧号、单数并过来、记录删掉。
 */
function MergeDialog({
  rows,
  onCancel,
  onDone,
}: {
  rows: Row[];
  onCancel: () => void;
  onDone: (msg: string) => void;
}) {
  const suggested = [...rows].sort(
    (a, b) => b.orderCount - a.orderCount || Number(b.lat != null) - Number(a.lat != null),
  )[0]!;
  const [keepId, setKeepId] = useState(suggested.id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const keep = rows.find((r) => r.id === keepId)!;
  const losers = rows.filter((r) => r.id !== keepId);

  async function doMerge() {
    setBusy(true);
    try {
      const r = await api<{ mergedCount: number; reportsMoved: number; oldPhonesAdded: string[] }>(
        '/contacts/merge',
        { method: 'POST', body: { keepId, mergeIds: losers.map((l) => l.id) } },
      );
      onDone(
        `已合并 ${r.mergedCount} 条到「${keep.name}」` +
          (r.oldPhonesAdded.length > 0 ? `,${r.oldPhonesAdded.join('、')} 存成了旧号` : ''),
      );
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '合并失败');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-label="合并收件人"
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          合并这 {rows.length} 条
        </h2>
        <p className="mt-1 text-sm text-slate-500">以哪条为准?留下的那条保留它的姓名、电话和地址</p>

        <div className="mt-3 space-y-2">
          {rows.map((r) => (
            <label
              key={r.id}
              className={`flex cursor-pointer gap-3 rounded-lg border p-3 text-sm ${
                r.id === keepId
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/40'
                  : 'border-slate-200 dark:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="keep"
                aria-label={`以 ${r.name} 为准`}
                checked={r.id === keepId}
                onChange={() => setKeepId(r.id)}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="font-medium text-slate-900 dark:text-slate-100">{r.name}</span>
                <span className="ml-2 font-mono text-xs text-slate-500">{r.phone}</span>
                <span className="block text-slate-600 dark:text-slate-300">{r.address}</span>
                <span className="block text-xs text-slate-400">
                  下过 {r.orderCount} 单
                  {r.lat != null ? ' · 有坐标' : ' · 没有坐标(用它建的单上不了地图)'}
                  {r.errorTag && ` · ${contactErrorLabel(r.errorTag)}`}
                </span>
              </span>
            </label>
          ))}
        </div>

        {/*
          ★ 逐条写清楚会发生什么 —— 尤其是「旧号」那一条:
            不说的话,人会以为合并等于把另一个号码扔了,
            而那个号码正是对方可能用来打进来的那个。
        */}
        <ul
          className="mt-4 space-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600
                       dark:bg-slate-900/60 dark:text-slate-300"
        >
          <li>
            · 留下「{keep.name}」这条,其余 {losers.length} 条删掉
          </li>
          <li>
            · {losers.map((l) => l.phone).join('、')} 存进旧号 —— 以后拿这些号码搜,照样搜得到「
            {keep.name}」
          </li>
          <li>
            · 下过的单数加到一起({rows.reduce((n, r) => n + r.orderCount, 0)}{' '}
            单),司机报过的问题也挂过来
          </li>
          <li>· 历史订单上写的是下单当时的收件人,不受影响</li>
        </ul>

        {err && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void doMerge()}
            disabled={busy || losers.length === 0}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '合并中…' : `合并成「${keep.name}」`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 删除前的确认。
 *
 * ★ 把**名字一条条摆出来**,不只报个数目。
 *   「确定删除 7 条?」回答不了「是哪 7 条」——
 *   而勾是一路点下来的,中间点错一下人自己不会知道。
 *
 * ★ 下过单的那些单独标出来:通讯录里越老的记录越像垃圾
 *   (名字潦草、地址是简写),而下过五十单的那条恰恰是最不能删的。
 */
function DeleteConfirmDialog({
  rows,
  onCancel,
  onConfirm,
}: {
  rows: Row[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const used = rows.filter((r) => r.orderCount > 0);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-label="删除收件人"
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          删除这 {rows.length} 条?
        </h2>
        <ul className="mt-3 space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.id} className="text-slate-700 dark:text-slate-200">
              {r.name}
              <span className="ml-2 font-mono text-xs text-slate-400">{r.phone}</span>
              {r.orderCount > 0 && (
                <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                  下过 {r.orderCount} 单
                </span>
              )}
            </li>
          ))}
        </ul>
        {used.length > 0 && (
          <p
            className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800
                        dark:bg-amber-950/40 dark:text-amber-300"
          >
            其中 {used.length} 条下过单。历史订单上的收件人是下单时存的一份,
            删了不影响那些单子;但下次这个人再打来,得重新问一遍地址。
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
          >
            删除
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 错误标记。
 *
 * ★ 送不到的原因翻来覆去就两种:号码打不通、地址找不到。
 *   标一下的成本是一次点击,不标的代价是**下一个人再跑一趟**。
 */
function ErrorTagDialog({
  row,
  onClose,
  onSaved,
}: {
  row: Row;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [tag, setTag] = useState<string | null>(row.errorTag);
  const [note, setNote] = useState(row.errorNote ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    try {
      await api(`/contacts/${row.id}/error-tag`, {
        method: 'POST',
        body: { errorTag: tag, errorNote: note || null },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="标记有问题"
        className="w-full max-w-md rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">标记有问题</h2>
        <p className="mt-1 text-sm text-slate-500">
          标过之后,建单页搜到这个人时会当场提示 —— 在单建出去之前
        </p>

        <div className="mt-4 space-y-2">
          {CONTACT_ERROR_TAG_KEYS.map((k) => (
            <label key={k} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="errorTag"
                className="mt-1"
                checked={tag === k}
                onChange={() => setTag(k)}
              />
              <span>
                <span className="text-slate-900 dark:text-slate-100">
                  {CONTACT_ERROR_TAGS[k].label}
                </span>
                <span className="block text-xs text-slate-500">
                  {CONTACT_ERROR_TAGS[k].description}
                </span>
                {/* ★ 「标了之后会怎样」要写出来,否则没人知道该不该标 */}
                <span className="block text-xs text-slate-400">
                  {CONTACT_ERROR_TAGS[k].whenSeen}
                </span>
              </span>
            </label>
          ))}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="errorTag"
              className="mt-1"
              checked={tag === null}
              onChange={() => setTag(null)}
            />
            <span className="text-slate-900 dark:text-slate-100">没问题(撤销标记)</span>
          </label>
        </div>

        <label className="mt-3 block text-xs text-slate-500" htmlFor="tag-note">
          备注
        </label>
        <input
          id="tag-note"
          aria-label="备注"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="比如:8 月 12 号打过,已停机"
          className={`${INPUT} mt-1 w-full`}
        />

        {err && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 新增收件人。
 *
 * ★ 字段和建单页那一段**完全对齐**(姓名 / 手机号 / 地址 + 地址搜索),
 *   因为录的是同一件事 —— 客服在两个地方要填的东西不该长得不一样。
 *
 * ★ 不选商家:这里建的是**散客记录**(customer_id 为空),
 *   而搜索的商家过滤是「这家的 + 散客的」—— 所以建单时选哪个商家都搜得到。
 *   挂到某个商家名下反而会让别的商家搜不着,于是同一个人被重录好几遍。
 */
function CreateDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (r: { phone: string; revived: boolean }) => Promise<void> | void;
}) {
  const [f, setF] = useState({
    name: '',
    phone: '',
    address: '',
    addressDetail: '',
    // 派送备注 —— 跟着人的长期交代,每次下单自动带到订单上
    deliveryNote: '',
  });
  /*
   * ★ 坐标只能靠**选中地址候选**拿到,不给手填的框。
   *   建单页也是这个规矩:手抄经纬度这件事没有人做得对,
   *   而抄错的后果是司机被导到另一个区。
   *   (真要手改坐标,列表上「编辑」那一栏还在。)
   */
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * ★ 这个号码和地址下已经有别的名字时,把那条摆出来 ——
   *   同一个人就用那条,两个人共用号码地址(合租、公司总机)就另存一条。
   */
  const [clash, setClash] = useState<{
    id: string;
    name: string;
    address: string;
    orderCount: number;
  } | null>(null);

  const ready = f.name.trim() !== '' && f.phone.trim() !== '' && f.address.trim() !== '';

  async function save(renameExisting = false) {
    if (!ready) return;
    setBusy(true);
    setClash(null);
    try {
      const r = await api<{ id: string; revived: boolean }>('/contacts', {
        method: 'POST',
        body: {
          name: f.name.trim(),
          phone: f.phone.trim(),
          address: f.address.trim(),
          addressDetail: f.addressDetail.trim() || null,
          deliveryNote: f.deliveryNote.trim() || null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          // 坐标是人从候选里挑的,不是异步编码猜的 —— 这条不用再进复核队列
          addressVerified: coords != null,
          // 人选了「用我填的这个名字」—— 去更新已有的那一条
          ...(renameExisting ? { renameExisting: true } : {}),
        },
      });
      await onSaved({ phone: f.phone.trim(), revived: r.revived });
    } catch (e) {
      /*
       * ★ 这个号码和地址下已经有**别的名字**时,后端不再静默覆盖了 ——
       *   在此之前它会把那条改成现在填的名字:通讯录里是「张三」,
       *   建「李四」就把张三抹掉了,而历史订单还指着那条记录。
       *   现在摆出来让人选:同一个人就用那条,两个人就另存一条。
       */
      const d = e instanceof ApiFailure ? ((e.error.details ?? {}) as Record<string, unknown>) : {};
      if (d['conflictId']) {
        setClash({
          id: String(d['conflictId']),
          name: String(d['conflictName'] ?? ''),
          address: String(d['conflictAddress'] ?? ''),
          orderCount: Number(d['conflictOrderCount'] ?? 0),
        });
        setErr(null);
      } else {
        setErr(e instanceof ApiFailure ? e.message : '保存失败');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="新增收件人"
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">新增收件人</h2>
        <p className="mt-1 text-sm text-slate-500">
          和建单页填的是同一份东西 —— 存好之后,建单时搜名字或号码就能整条带出来
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Labeled label="姓名">
            <input
              aria-label="姓名"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className={`${INPUT} w-full`}
            />
          </Labeled>
          <Labeled label="手机号">
            <input
              aria-label="手机号"
              value={f.phone}
              onChange={(e) => setF({ ...f, phone: e.target.value })}
              placeholder="会按租户所在国家补区号"
              className={`${INPUT} w-full`}
            />
          </Labeled>
          <div className="sm:col-span-2">
            <Labeled label="地址">
              <input
                aria-label="地址"
                value={f.address}
                onChange={(e) => {
                  setF({ ...f, address: e.target.value });
                  /*
                   * ★ 地址一改,之前挑中的坐标就作废。
                   *   留着的话:挑了 A 街的坐标、又把地址改成 B 街,
                   *   存进去就是「B 街的名字配 A 街的坐标」——
                   *   而这种单看起来一切正常,直到司机开到了 A 街。
                   */
                  setCoords(null);
                }}
                className={`${INPUT} w-full`}
              />
            </Labeled>
            <AddressSearchBox
              query={f.address}
              onPick={(c) => {
                setF((s) => ({ ...s, address: c.address }));
                setCoords({ lat: c.lat, lng: c.lng });
              }}
            />
          </div>
          <div className="sm:col-span-2">
            <Labeled label="门牌 / 楼层(可不填)">
              <input
                aria-label="门牌 / 楼层"
                value={f.addressDetail}
                onChange={(e) => setF({ ...f, addressDetail: e.target.value })}
                placeholder="比如:12A 座 3 楼"
                className={`${INPUT} w-full`}
              />
            </Labeled>
          </div>
          {/*
            ★ 建的时候就能填。
              这里原来只有 state 和提交,没有输入框 —— 也就是说「派送备注」
              只能建完之后再进编辑补一次,而客服不会为了一句门禁密码
              多开一次弹窗;那这个字段在实际使用里就等于不存在。
          */}
          <div className="sm:col-span-2">
            <Labeled label="派送备注(每次下单自动带上,可不填)">
              <input
                aria-label="派送备注"
                value={f.deliveryNote}
                onChange={(e) => setF({ ...f, deliveryNote: e.target.value })}
                placeholder="例如:大门密码 1234,走侧门;白天放前台"
                className={`${INPUT} w-full`}
              />
            </Labeled>
          </div>
        </div>

        {/*
          ★ 坐标状态摆在保存按钮上方,而不是存完再说。
            没坐标照样能存(有总比没有强),但得让人在**还能补**的时候知道 ——
            这一屏顶上那句「有多少条没坐标」数的就是这些。
        */}
        <div className="mt-3 text-xs" aria-live="polite">
          {coords ? (
            <span className="text-green-600 dark:text-green-400">
              坐标已带上 {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
            </span>
          ) : (
            <span className="text-amber-600 dark:text-amber-400">
              还没有坐标 —— 在上面的候选里挑一条地址就有了。没有坐标的人,建出来的单上不了地图
            </span>
          )}
        </div>

        {clash && (
          <div
            aria-label="这个号码和地址已经有记录"
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm
                       dark:border-amber-800 dark:bg-amber-950/40"
          >
            {/*
              ★ 电话和地址都一样 —— 那就是同一个人,只是名字写法不同
                (「王先生」/「王小明」/「王太太」)。所以问的是**用哪个名字**,
                不是「要不要再存一条」。
                真的是两个人的话电话会不一样,而电话在去重键里,根本撞不上。

              ★ 在此之前这里是**静默覆盖**:直接把「张三」改成你填的名字,
                而历史订单还指着那条记录 —— 那些单的收件人凭空换了人,
                没有任何地方提过这件事。
            */}
            <div className="text-amber-900 dark:text-amber-200">
              这个号码和地址下已经有一条记录:
              <span className="font-medium">{clash.name}</span>
              {clash.orderCount > 0 && `(下过 ${clash.orderCount} 单)`}
              ,而你填的是 <span className="font-medium">{f.name.trim()}</span>
            </div>
            <div className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
              同一个人的两种写法 —— 用哪个?
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => void save(true)}
                disabled={busy}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white
                           disabled:opacity-40"
              >
                {busy ? '处理中…' : `改成「${f.name.trim()}」`}
              </button>
              <button
                onClick={onClose}
                className="rounded-lg border border-amber-500 px-3 py-1.5 text-xs font-medium
                           text-amber-800 dark:text-amber-300"
              >
                保持「{clash.name}」
              </button>
            </div>
          </div>
        )}

        {err && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !ready}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditDialog({
  row,
  onClose,
  onSaved,
}: {
  row: Row;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState({
    name: row.name,
    phone: row.phone,
    address: row.address,
    addressDetail: row.addressDetail ?? '',
    deliveryNote: row.deliveryNote ?? '',
    lat: row.lat == null ? '' : String(row.lat),
    lng: row.lng == null ? '' : String(row.lng),
  });
  const [wrongPhones, setWrongPhones] = useState<string[]>(row.wrongPhones ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * ★ 撞上另一条一模一样的记录时,光说「已经有这个联系人了」没有用 ——
   *   人要做的**正是**把这两条理干净,而他既不知道和谁撞了,也没有下一步。
   *   所以把那一条摆出来,并给一条出路:先把它停用。
   */
  const [clash, setClash] = useState<{
    id: string;
    name: string;
    address: string;
    orderCount: number;
  } | null>(null);

  async function save() {
    setBusy(true);
    setClash(null);
    try {
      await api(`/contacts/${row.id}`, {
        method: 'PATCH',
        body: {
          name: f.name.trim(),
          phone: f.phone.trim(),
          wrongPhones,
          address: f.address.trim(),
          addressDetail: f.addressDetail.trim() || null,
          // ★ 漏了这一行的后果是:填得进、看得见、存不下 ——
          //   重新打开又是空的,而人只会以为是自己没点保存
          deliveryNote: f.deliveryNote.trim() || null,
          lat: f.lat === '' ? null : Number(f.lat),
          lng: f.lng === '' ? null : Number(f.lng),
        },
      });
      await onSaved();
    } catch (e) {
      const d = e instanceof ApiFailure ? ((e.error.details ?? {}) as Record<string, unknown>) : {};
      if (d['conflictId']) {
        setClash({
          id: String(d['conflictId']),
          name: String(d['conflictName'] ?? ''),
          address: String(d['conflictAddress'] ?? ''),
          orderCount: Number(d['conflictOrderCount'] ?? 0),
        });
        setErr(null);
      } else {
        setErr(e instanceof ApiFailure ? e.message : '保存失败');
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * 把挡路的那条停用 —— 停用之后它不再占去重键,这一条就存得下了。
   *
   * ★ 走的是 PATCH 而不是 DELETE:DELETE 现在是**删除**(软删,写 deleted_at)。
   *   这里发生的事不是「这条该删」,而是「它挡着我保存」——
   *   替人做的决定越小越好,所以只关掉 is_active,
   *   `deleted_at` 留给人自己按的那颗删除按钮。
   */
  async function disableClash() {
    if (!clash) return;
    setBusy(true);
    try {
      await api(`/contacts/${clash.id}`, { method: 'PATCH', body: { isActive: false } });
      setClash(null);
      await save();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '停用失败');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="编辑收件人"
        className="w-full max-w-lg rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">编辑收件人</h2>
        {/*
          ★ 换号码不会把旧号码丢掉,而是收进「旧号」——
            对方拿旧号打进来时还搜得到人,而那恰恰是最需要搜到的时候。
        */}
        <p className="mt-1 text-sm text-slate-500">
          改了号码的话,旧号码会留在「旧号」里,照样搜得到
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Labeled label="姓名">
            <input
              aria-label="姓名"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className={`${INPUT} w-full`}
            />
          </Labeled>
          <Labeled label="电话">
            <input
              aria-label="电话"
              value={f.phone}
              onChange={(e) => setF({ ...f, phone: e.target.value })}
              className={`${INPUT} w-full font-mono`}
            />
          </Labeled>
          {wrongPhones.length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  历史旧号（共 {wrongPhones.length} 个）
                </span>
                <span className="text-[11px] text-slate-400">
                  客户打入旧号时系统仍可匹配
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {wrongPhones.map((p) => (
                  <div
                    key={p}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-mono text-slate-700 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  >
                    <span className="select-all">{p}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setF((prev) => ({ ...prev, phone: p }));
                      }}
                      className="ml-1 text-[11px] font-sans text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                      title="将此旧号设为当前主电话"
                    >
                      设为主号
                    </button>
                    <button
                      type="button"
                      onClick={() => setWrongPhones((prev) => prev.filter((x) => x !== p))}
                      className="ml-1 font-sans text-sm leading-none text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                      title="移除此旧号"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="sm:col-span-2">
            <Labeled label="地址">
              <input
                aria-label="地址"
                value={f.address}
                onChange={(e) =>
                  /*
                   * ★ 地址一改,原来那份坐标就作废。
                   *   留着的话存进去是「B 街的名字配 A 街的坐标」——
                   *   这种记录看起来一切正常,直到司机开到了 A 街。
                   */
                  setF({ ...f, address: e.target.value, lat: '', lng: '' })
                }
                className={`${INPUT} w-full`}
              />
            </Labeled>
            {/*
              ★ 编辑这里原来**没有**地址搜索,只有两个手填经纬度的框。
                新增那边有搜索,编辑没有 —— 同一件事两种做法,
                而改地址恰恰是最需要重新定位的时候。
                用户原话:「编辑的时候地址没有像添加一样的搜索功能」。
            */}
            <AddressSearchBox
              query={f.address}
              onPick={(c) =>
                setF((s0) => ({
                  ...s0,
                  address: c.address,
                  lat: String(c.lat),
                  lng: String(c.lng),
                }))
              }
            />
          </div>
          <div className="sm:col-span-2">
            <Labeled label="门牌 / 楼层">
              <input
                aria-label="门牌 / 楼层"
                value={f.addressDetail}
                onChange={(e) => setF({ ...f, addressDetail: e.target.value })}
                className={`${INPUT} w-full`}
              />
            </Labeled>
          </div>
          {/*
            ★ 「派送备注」是**跟着人**的,不是跟着某一单的。
              门禁密码、走侧门、放前台 —— 这些每次送都成立,
              而在这之前只能每次建单时重新打一遍,而客服多半不会打。
 
            ★ 它会被**快照**到每一张新单上,和那一单临时填的备注并列显示。
              改了之后只影响以后的单:已经送完的那些依据的是当时那句话。
          */}
          <div className="sm:col-span-2">
            <Labeled label="派送备注(每次下单自动带上)">
              <input
                aria-label="派送备注"
                value={f.deliveryNote}
                onChange={(e) => setF({ ...f, deliveryNote: e.target.value })}
                placeholder="例如:大门密码 1234,走侧门;白天放前台"
                className={`${INPUT} w-full`}
              />
            </Labeled>
          </div>

          {/*
            ★ 经纬度不再是两个输入框,只报状态。
              用户原话:「编辑的时候不用显示经纬度吧」—— 他是对的:
              手抄经纬度这件事没有人做得对,而抄错的后果是司机被导到另一个区。
              坐标从上面的地址候选里来。

            ★ 但**不能连看都不给看**:这一页顶上写着「有 N 条没有坐标 ——
              用它们建的单上不了地图」,而补坐标正是这一屏的核心用途之一。
              没有坐标时要说出来,人才知道这一条还差什么。
          */}
          <div className="sm:col-span-2 text-xs" aria-live="polite">
            {f.lat !== '' && f.lng !== '' ? (
              <span className="text-green-600 dark:text-green-400">
                坐标 {Number(f.lat).toFixed(4)}, {Number(f.lng).toFixed(4)}
              </span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">
                还没有坐标 —— 在上面的候选里挑一条地址就有了。没有坐标的人,建出来的单上不了地图
              </span>
            )}
          </div>
        </div>

        {clash && (
          <div
            aria-label="和另一条记录重复"
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm
                       dark:border-amber-800 dark:bg-amber-950/40"
          >
            <div className="text-amber-900 dark:text-amber-200">
              这个号码下已经有一条一样的记录:
              <span className="font-medium">{clash.name}</span> · {clash.address}
              {clash.orderCount > 0 && `(下过 ${clash.orderCount} 单)`}
            </div>
            {/*
              ★ 给出路,而不是只说「不行」。
                通讯录里同一个人有两条本来就是要清理的 ——
                停用那条之后它不再占去重键,这一条就存得下了。
                历史订单还指着它,所以是停用不是删除。
            */}
            {/*
              ★ 编辑撞上另一条,意味着这两条**本来就是重复的** ——
                这一条改完之后和那一条一模一样。出路是把那条停用,
                历史订单还指着它,所以是停用不是删除。
            */}
            <div className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
              要么把这一条改成别的地址,要么把那一条停用 —— 它的历史订单不会受影响
            </div>
            <button
              onClick={() => void disableClash()}
              disabled={busy}
              className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '处理中…' : '停用那一条,保存这一条'}
            </button>
          </div>
        )}

        {err && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs text-slate-500">{label}</div>
      {children}
    </div>
  );
}
